import React, { useContext, useMemo, useState } from 'react';
import { CloudSyncOutlined } from '@ant-design/icons';
import { Alert, Button, Flex, Space, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';

import {
  ALL_SENSORS,
  RefreshStatus,
} from '@totem/components/administration/reconciliation/nozomi/vantage/types';
import {
  buildRows,
  getCountedRange,
} from '@totem/components/administration/reconciliation/nozomi/vantage/utilities';
import VantageAssetCountsContext from '@totem/components/administration/reconciliation/nozomi/vantage/VantageAssetCountsContext';
import VantageAssetCountsTable from '@totem/components/administration/reconciliation/nozomi/vantage/VantageAssetCountsTable';

const REFRESH_LABELS: Record<RefreshStatus, string> = {
  pending: 'Queued…',
  running: 'Counting…',
};

export const VantageAssetCountsExtra = () => {
  const { data, refreshing, onRefreshCounts } = useContext(
    VantageAssetCountsContext,
  );
  const range = useMemo(() => getCountedRange(data?.sensors ?? []), [data]);
  const allStatus = refreshing[ALL_SENSORS];

  return (
    <Space>
      {range && (
        <Typography.Text type="secondary">
          Counted {dayjs(range.oldest).format('MMM D, h:mm A')}
          {range.newest.getTime() - range.oldest.getTime() > 60000 &&
            ` – ${dayjs(range.newest).format('MMM D, h:mm A')}`}
        </Typography.Text>
      )}
      <Tooltip title="Count every sensor's assets straight from Vantage. This runs in the background and can take a few minutes.">
        <Button
          type="primary"
          icon={<CloudSyncOutlined />}
          loading={!!allStatus}
          onClick={() => onRefreshCounts()}
        >
          {allStatus ? REFRESH_LABELS[allStatus] : 'Recount All Sensors'}
        </Button>
      </Tooltip>
    </Space>
  );
};

export const VantageAssetCountsLayout = () => {
  const { data } = useContext(VantageAssetCountsContext);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);

  const organizationKeys = useMemo(
    () => buildRows(data?.sensors ?? []).map((row) => row.key),
    [data],
  );

  return (
    <Flex vertical gap={16}>
      <Alert
        type="info"
        showIcon
        message="These counts come straight from the Vantage API, not from portal data."
        description="Recount a sensor, or all sensors, to see the current state in Vantage. Counting runs in the background; the page updates when it finishes. Total should match the Assets count in Vantage with Last activity time set to All."
      />
      <Flex justify="flex-end" gap={8}>
        <Button onClick={() => setExpandedKeys(organizationKeys)}>
          Expand All
        </Button>
        <Button onClick={() => setExpandedKeys([])}>Collapse All</Button>
      </Flex>
      <VantageAssetCountsTable
        expandedKeys={expandedKeys}
        onExpandedKeysChange={setExpandedKeys}
      />
    </Flex>
  );
};

export default VantageAssetCountsLayout;
